// app/javascript/pages/Admin/Themes/Index.tsx
import React, { useState } from 'react'
import { router } from '@inertiajs/react'
import { AppShell } from '@/components/shared/AppShell'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from '@/components/ui/dialog'
import { Sparkles, Plus, Moon, Sun, Check, Trash2, Edit2, ShieldAlert } from 'lucide-react'
import type { AppThemeDefinition } from '@/types'

interface Preset {
  name: string
  display_name: string
  mode: 'light' | 'dark'
  swatches: [string, string, string]
}

interface Props {
  themes: AppThemeDefinition[]
  presets: Preset[]
}

export default function ThemesIndex({ themes, presets }: Props) {
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [selectedPreset, setSelectedPreset] = useState<Preset | null>(null)
  const [customName, setCustomName] = useState('')
  const [customDisplayName, setCustomDisplayName] = useState('')

  const handleToggleActive = (theme: AppThemeDefinition) => {
    router.patch(`/admin/themes/${theme.id}`, {
      theme: { is_active: !theme.is_active }
    })
  }

  const handleCreate = () => {
    if (!selectedPreset) return
    const name = customName.trim() || `${selectedPreset.name}-custom-${Date.now()}`
    const displayName = customDisplayName.trim() || `${selectedPreset.display_name} (Custom)`

    router.post('/admin/themes', {
      theme: {
        name,
        display_name: displayName,
        mode: selectedPreset.mode,
        is_active: true,
        sort_order: themes.length + 1,
        metadata: { swatches: selectedPreset.swatches }
      }
    }, {
      onSuccess: () => {
        setIsCreateOpen(false)
        setSelectedPreset(null)
        setCustomName('')
        setCustomDisplayName('')
      }
    })
  }

  const handleDelete = (themeId: number) => {
    if (confirm('Are you sure you want to delete this custom theme?')) {
      router.delete(`/admin/themes/${themeId}`)
    }
  }

  return (
    <AppShell>
      <div className="space-y-6 max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-glass border-glass-border p-6 rounded-2xl shadow-glass-sm">
          <div>
            <h1 className="text-2xl font-extrabold text-foreground tracking-tight flex items-center gap-2.5">
              <Sparkles className="h-6 w-6 text-secondary" /> Appearance & Theme System
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Manage application color schemes and fluid glass themes available to students and administrators.
            </p>
          </div>

          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary text-primary-fg hover:bg-primary/90 shadow-glow-primary">
                <Plus className="mr-2 h-4 w-4" /> Create New Theme
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg bg-glass backdrop-blur-glass border-glass-border shadow-glass-lg">
              <DialogHeader>
                <DialogTitle className="text-lg font-bold text-foreground">Create Theme from Preset Palette</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-3">
                <div className="space-y-2">
                  <Label>1. Choose Base Color Preset</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto pr-1">
                    {presets.map((p) => (
                      <button
                        key={p.name}
                        type="button"
                        onClick={() => {
                          setSelectedPreset(p)
                          setCustomDisplayName(`${p.display_name} Custom`)
                          setCustomName(`${p.name}-custom`)
                        }}
                        className={`flex items-center gap-3 p-2.5 rounded-xl border text-left transition-all ${
                          selectedPreset?.name === p.name
                            ? 'border-primary bg-primary/15 ring-1 ring-primary'
                            : 'border-border/60 bg-card/60 hover:bg-muted/40'
                        }`}
                      >
                        <div className="flex items-center -space-x-1 shrink-0">
                          <span className="w-4 h-4 rounded-full border border-black/10" style={{ backgroundColor: p.swatches[0] }} />
                          <span className="w-4 h-4 rounded-full border border-black/10" style={{ backgroundColor: p.swatches[1] }} />
                          <span className="w-4 h-4 rounded-full border border-black/10" style={{ backgroundColor: p.swatches[2] }} />
                        </div>
                        <span className="text-xs font-bold text-foreground truncate">{p.display_name}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {selectedPreset && (
                  <>
                    <div className="space-y-1.5">
                      <Label>Display Name</Label>
                      <Input
                        value={customDisplayName}
                        onChange={(e) => setCustomDisplayName(e.target.value)}
                        placeholder="e.g. Solarized Special"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Machine Identifier (slug)</Label>
                      <Input
                        value={customName}
                        onChange={(e) => setCustomName(e.target.value)}
                        placeholder="e.g. solarized-special"
                      />
                    </div>
                  </>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
                <Button onClick={handleCreate} disabled={!selectedPreset}>Save & Publish</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Theme List Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {themes.map((theme) => {
            const swatches = theme.metadata?.swatches || ['#888', '#999', '#eee']
            const isBuiltin = ['glass-light', 'dark-pro', 'solarized-light', 'atom-dark', 'aurora'].includes(theme.name)

            return (
              <Card key={theme.id || theme.name} className="relative overflow-hidden group">
                <CardHeader className="pb-3 border-b border-border/40">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CardTitle className="text-base font-extrabold">{theme.display_name}</CardTitle>
                      {isBuiltin && (
                        <Badge variant="outline" className="text-[10px] uppercase font-bold text-muted-foreground border-border/60">
                          Built-in
                        </Badge>
                      )}
                    </div>
                    <Badge variant={theme.mode === 'dark' ? 'secondary' : 'outline'} className="text-xs font-semibold gap-1">
                      {theme.mode === 'dark' ? <Moon className="h-3 w-3 text-secondary" /> : <Sun className="h-3 w-3 text-tertiary" />}
                      <span className="capitalize">{theme.mode}</span>
                    </Badge>
                  </div>
                </CardHeader>

                <CardContent className="p-5 space-y-4">
                  {/* Color Swatch Bar */}
                  <div className="h-12 rounded-xl p-2 bg-muted/40 border border-border/40 flex items-center justify-between">
                    <span className="text-xs font-semibold text-muted-foreground">Palette Swatches</span>
                    <div className="flex items-center gap-2">
                      {swatches.map((s, idx) => (
                        <div
                          key={idx}
                          className="w-7 h-7 rounded-lg shadow-xs border border-black/10 transition-transform group-hover:scale-105"
                          style={{ backgroundColor: s }}
                          title={`Color ${idx + 1}: ${s}`}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Actions & Active Toggle */}
                  <div className="flex items-center justify-between pt-2 border-t border-border/30">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={theme.is_active}
                        onCheckedChange={() => handleToggleActive(theme)}
                        id={`theme-active-${theme.id}`}
                      />
                      <Label htmlFor={`theme-active-${theme.id}`} className="text-xs font-semibold cursor-pointer">
                        {theme.is_active ? 'Active' : 'Inactive'}
                      </Label>
                    </div>

                    {!isBuiltin && theme.id && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(theme.id!)}
                        className="h-8 w-8 text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>
      </div>
    </AppShell>
  )
}
